import { useCallback, useRef } from 'react';

import { Warn } from '@utilities/logging';

export interface ArrowUpdateManager {
  register: (id: string, updateFn: () => void) => void;
  unregister: (id: string) => void;
  updateAll: () => void;
}

let updateTimeout: NodeJS.Timeout | null = null;

const arrowManager: ArrowUpdateManager = {
  updateFunctions: new Map<string, () => void>(),

  register(id: string, updateFn: () => void) {
    this.updateFunctions.set(id, updateFn);
  },

  unregister(id: string) {
    this.updateFunctions.delete(id);
  },

  updateAll() {
    // Debounce updates to prevent excessive re-renders
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    updateTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        this.updateFunctions.forEach((updateFn, id) => {
          try {
            updateFn();
          } catch (error) {
            Warn(`Error updating arrow ${id}:`, error);
          }
        });
      });
    }, 50);
  },
} as ArrowUpdateManager & { updateFunctions: Map<string, () => void> };

export const useCustomArrows = () => {
  const updateAll = useCallback(() => {
    arrowManager.updateAll();
  }, []);

  return updateAll;
};

export const useArrowUpdate = (arrowId: string, updateFn: () => void) => {
  const updateFnRef = useRef(updateFn);

  updateFnRef.current = updateFn;

  const stableUpdateFn = useCallback(() => {
    updateFnRef.current();
  }, []);

  const register = useCallback(() => {
    arrowManager.register(arrowId, stableUpdateFn);
  }, [arrowId, stableUpdateFn]);

  const unregister = useCallback(() => {
    arrowManager.unregister(arrowId);
  }, [arrowId]);

  return { register, unregister };
};
